import "../features/eex/operations-pages.css";
import { createFileRoute } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Fragment, useState } from "react";
import { api } from "../lib/api";
import { cn } from "../lib/utils";
import { invalidateEexData } from "../lib/eex-invalidate";
import { Td, Th, errMessage, withRecognitionConfirm } from "../components/eex/ui";
import { useAuth } from "../stores/auth";
import { useEexRole } from "../components/eex/use-eex-role";
import { MatchingPanel } from "../components/eex/MatchingPanel";
import { ReclassDialog, type ReclassTarget } from "../components/eex/ReclassDialog";
import { fmtEur0 } from "../lib/eex-format";
import { desktopOnlyRoute } from "../components/ui/mobile-availability-page";

export const Route = createFileRoute("/eex/matching")({
  component: desktopOnlyRoute(EexMatchingPage),
});

// The matching WORKLIST (owner design 2026-07-05): every open matching decision
// in the book — (book, trader, contract) cells with opposing uncommitted lots —
// with what recognition books BY DEFAULT per month if nothing is done. Filter
// by booking month (the pre-close review), expand a row into the T panel, match
// where the default tells the wrong story. A row disappears once decided.

interface WorklistRow {
  // real = pairable opposition · carried = real fills vs cascade parcels ·
  // delivering = NO decision here, shown so default bookings can't hide.
  kind: "real" | "carried" | "delivering";
  code: string;
  expYear: number;
  expMonth: number;
  product: string;
  period: string;
  book: string | null;
  trader: string | null;
  openBuys: number;
  openSells: number;
  matchable: number;
  carriedLots?: number;
  realLots?: number;
  hasCarried: boolean;
  months: { month: string; eur: number }[];
}
interface PendingPin {
  id: string;
  realTradeId: string;
  parcelRootTradeId: string;
  lots: number;
  note: string | null;
  requestedBy: string | null;
  requestedByName: string;
  createdAt: string;
}
interface PendingProposal {
  id: string;
  name: string;
  note: string | null;
  book: string | null;
  trader: string | null;
  product: string | null;
  period: string | null;
  buyLots: number;
  sellLots: number;
  requestedBy: string | null;
  requestedByName: string;
  // A leg was edited or removed since the request — approval would land on a
  // picture that no longer exists; needs a fresh proposal.
  stale: boolean;
}
interface WorklistResponse {
  recognitionScopeNotice?: string;
  asOf: string;
  months: { month: string; eur: number }[];
  rows: WorklistRow[];
  pendingParcelPins: PendingPin[];
  pendingMatchProposals: PendingProposal[];
}
interface ReclassEntry {
  id: string;
  name: string;
  reason: string;
  periodLabel: { product: string; period: string };
  lots: number;
  targetMonth: string;
  targetBook: string | null;
  targetTrader: string | null;
  amountEur: number;
  status: "active" | "reversed";
  stale: boolean;
  createdByName: string | null;
  reversedByName: string | null;
  reversalReason: string | null;
  createdAt: string;
  reversedAt: string | null;
}

const MONTHS_ABBR = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
const mLabel = (ym: string): string => {
  const [y, m] = ym.split("-");
  return `${MONTHS_ABBR[Number(m) - 1] ?? m}-${y!.slice(2)}`;
};
const eurClass = (v: number): string => (v > 0 ? "text-posGreen" : v < 0 ? "text-negRed" : "text-muted");
// Compact EUR for chips: −5.77M / +146k / −44
const fmtCompact = (v: number): string => {
  const a = Math.abs(v);
  const sign = v < 0 ? "−" : "+";
  if (a >= 1_000_000) return `${sign}${(a / 1_000_000).toFixed(2)}M`;
  if (a >= 1_000) return `${sign}${Math.round(a / 1_000)}k`;
  return `${sign}${Math.round(a)}`;
};

function EexMatchingPage() {
  const qc = useQueryClient();
  const { canWrite, isAdmin } = useEexRole();
  const myUserId = useAuth((s) => s.user?.userId);
  const [monthFilter, setMonthFilter] = useState<string | null>(null);
  const [bookFilter, setBookFilter] = useState<string>("");
  const [open, setOpen] = useState<string | null>(null);

  const q = useQuery({
    queryKey: ["eex-worklist"],
    queryFn: () => api<WorklistResponse>("/eex/recognition/worklist"),
  });
  const [approvalMsg, setApprovalMsg] = useState<string | null>(null);
  const invalidate = () => {
    setApprovalMsg(null);
    // An approval re-times recognition AND stamps a pair label, so the positions
    // tree/leaf, allocatable lots and match preview all move too. Bust every eex-*
    // query via the shared prefix helper — a hand-listed set here silently missed
    // ["eex-positions-tree"]/["eex-positions-leaf"], leaving the drill-down stale.
    invalidateEexData(qc);
  };
  const actPin = useMutation({
    mutationFn: (v: { id: string; action: "approve" | "reject" }) =>
      api(`/eex/recognition/parcel-pins/${v.id}/${v.action}`, { method: "POST" }),
    onSuccess: invalidate,
    onError: (e) => setApprovalMsg(errMessage(e)),
  });
  const actProposal = useMutation({
    mutationFn: (v: { id: string; action: "approve" | "reject" }) =>
      api(`/eex/recognition/match-proposals/${v.id}/${v.action}`, { method: "POST" }),
    onSuccess: invalidate,
    onError: (e) => setApprovalMsg(errMessage(e)),
  });
  const withdrawProposal = useMutation({
    mutationFn: (v: { id: string }) =>
      api(`/eex/recognition/match-proposals/${v.id}`, { method: "DELETE" }),
    onSuccess: invalidate,
    onError: (e) => setApprovalMsg(errMessage(e)),
  });

  // P&L reclassification (admin journal layer): register + the anchor dialog.
  const [reclassTarget, setReclassTarget] = useState<ReclassTarget | null>(null);
  const [reclassProduct, setReclassProduct] = useState("");
  const [reclassPeriod, setReclassPeriod] = useState("");
  const [reclassMsg, setReclassMsg] = useState<string | null>(null);
  const reclassQ = useQuery({
    queryKey: ["eex-reclass"],
    queryFn: () => api<{ entries: ReclassEntry[] }>("/eex/recognition/reclass"),
    enabled: isAdmin,
  });
  const reverseReclass = useMutation({
    // Reverse is a booking act — collect the reason up front (the route
    // requires it), then let the confirm flow re-time booked months.
    mutationFn: async (v: { id: string }) => {
      const reason = window.prompt("Reason for reversing this reclassification (required — recorded in the audit trail):");
      if (!reason?.trim()) return null;
      return withRecognitionConfirm((extras) =>
        api(`/eex/recognition/reclass/${v.id}/reverse`, {
          method: "POST",
          body: JSON.stringify({ reason: reason.trim(), ...extras }),
        })
      );
    },
    onSuccess: () => {
      setReclassMsg(null);
      void qc.invalidateQueries({ queryKey: ["eex-reclass"] });
      invalidate();
    },
    onError: (e) => setReclassMsg(errMessage(e)),
  });

  if (q.isLoading) return <div className="p-4 text-muted text-sm">Loading worklist…</div>;
  if (q.isError) return <div className="p-4 text-negRed text-sm">{errMessage(q.error)}</div>;
  const data = q.data!;

  const books = [...new Set(data.rows.map((r) => r.book ?? "(no book)"))].sort();
  const filtered = data.rows
    .filter((r) => !monthFilter || r.months.some((m) => m.month === monthFilter))
    .filter((r) => !bookFilter || (r.book ?? "(no book)") === bookFilter)
    .sort((a, b) => {
      const impact = (r: WorklistRow) =>
        monthFilter
          ? Math.abs(r.months.find((m) => m.month === monthFilter)?.eur ?? 0)
          : r.months.reduce((s, m) => s + Math.abs(m.eur), 0);
      return impact(b) - impact(a);
    });
  const rowKey = (r: WorklistRow) =>
    [r.book ?? "", r.trader ?? "", r.code, r.expYear, r.expMonth].join("|");

  // Batch declared closes land as N pins sharing one tx timestamp — group ONE
  // request (requester + timestamp; an M×N batch may span several parcels)
  // into one approve-all decision; independent requests stay separate rows
  // (each approval still re-validates server-side).
  const pinGroups = [
    ...data.pendingParcelPins
      .reduce((m, p) => {
        const k = [p.requestedBy ?? "", p.createdAt].join("|");
        const list = m.get(k) ?? [];
        list.push(p);
        m.set(k, list);
        return m;
      }, new Map<string, PendingPin[]>())
      .values(),
  ];
  const actPinGroup = async (pins: PendingPin[], action: "approve" | "reject") => {
    // Each pin is independent — attempt them ALL (a rival edit may 409 one leg
    // of a multi-parcel batch without invalidating the rest). Report a summary;
    // any still-pending pins re-group and can be retried.
    let failed = 0;
    let lastErr = "";
    for (const p of pins) {
      try {
        await actPin.mutateAsync({ id: p.id, action });
      } catch (e) {
        failed += 1;
        lastErr = errMessage(e);
      }
    }
    invalidate();
    if (failed > 0)
      setApprovalMsg(`${failed} of ${pins.length} could not be ${action === "approve" ? "approved" : "rejected"}: ${lastErr}`);
  };

  return (
    <div className="operations-workspace ops-page ops-matching p-4 space-y-4">
      {data.recognitionScopeNotice && <p role="note" aria-label="Recognition scope" className="rounded border border-warnOrange/40 bg-warnOrange/10 p-3 text-sm">{data.recognitionScopeNotice}</p>}
      <header className="ops-heading">
        <h1 className="font-semibold text-lg">
          Matching
          {q.isFetching && !q.isLoading && (
            <span className="ml-2 text-[10px] font-normal text-muted">updating…</span>
          )}
        </h1>
        <p className="text-muted text-xs max-w-3xl">
          Open matching decisions — contracts with offsetting uncommitted lots. &ldquo;If
          unmatched&rdquo; is what recognition books by default (FIFO closes, deliveries at FSP);
          match where the default tells the wrong story. A row disappears once decided. As of{" "}
          {data.asOf}.
        </p>
      </header>

      {data.pendingParcelPins.length + data.pendingMatchProposals.length > 0 && (
        <div className="border border-amber-500/40 bg-amber-500/10 rounded px-3 py-2 text-xs space-y-1">
          <div className="font-medium text-amber-700 dark:text-amber-400">
            {data.pendingParcelPins.length + data.pendingMatchProposals.length} approval
            {data.pendingParcelPins.length + data.pendingMatchProposals.length === 1 ? "" : "s"} pending
          </div>
          {pinGroups.map((g) => {
            const roots = [...new Set(g.map((p) => p.parcelRootTradeId))];
            const lots = g.reduce((s, p) => s + p.lots, 0);
            return (
              <div key={g[0]!.id} className="flex items-center gap-2 flex-wrap">
                <span>
                  declared close{g.length === 1 ? "" : "s"} · {lots} lot(s)
                  {g.length > 1 ? ` in ${g.length} pairings` : ` · real ${g[0]!.realTradeId.slice(0, 8)}`} ⇄ carried{" "}
                  {roots.length === 1 ? roots[0]!.slice(0, 8) : `${roots.length} parcels`} · by {g[0]!.requestedByName}
                  {g[0]!.note ? ` — ${g[0]!.note}` : ""}
                </span>
                {isAdmin && (
                  <>
                    <button
                      className="text-posGreen hover:underline disabled:opacity-40"
                      disabled={actPin.isPending}
                      onClick={() => void actPinGroup(g, "approve")}
                    >
                      approve{g.length > 1 ? " all" : ""}
                    </button>
                    <button
                      className="text-negRed hover:underline disabled:opacity-40"
                      disabled={actPin.isPending}
                      onClick={() => void actPinGroup(g, "reject")}
                    >
                      reject{g.length > 1 ? " all" : ""}
                    </button>
                  </>
                )}
              </div>
            );
          })}
          {data.pendingMatchProposals.map((p) => (
            <div key={p.id} className="flex items-center gap-2 flex-wrap">
              <span>
                match <span className="font-medium">{p.name}</span>
                {p.product ? ` · ${p.product} ${p.period}` : ""} · {p.book ?? "(no book)"} · {p.trader ?? "—"} ·{" "}
                {p.buyLots} buy ⇄ {p.sellLots} sell · by {p.requestedByName}
                {p.note ? ` — ${p.note}` : ""}
              </span>
              {p.stale && (
                <span
                  className="px-1 py-0.5 rounded text-[10px] bg-negRed/15 text-negRed"
                  title="a leg changed since this was proposed — needs a fresh proposal"
                >
                  stale
                </span>
              )}
              {isAdmin ? (
                <>
                  <button
                    className="text-posGreen hover:underline disabled:opacity-40 disabled:cursor-not-allowed"
                    disabled={p.stale || actProposal.isPending}
                    title={p.stale ? "stale — the legs changed since this was proposed" : undefined}
                    onClick={() => actProposal.mutate({ id: p.id, action: "approve" })}
                  >
                    approve
                  </button>
                  <button className="text-negRed hover:underline" onClick={() => actProposal.mutate({ id: p.id, action: "reject" })}>
                    reject
                  </button>
                </>
              ) : (
                canWrite &&
                p.requestedBy === myUserId && (
                  <button className="text-muted hover:underline" onClick={() => withdrawProposal.mutate({ id: p.id })}>
                    withdraw
                  </button>
                )
              )}
            </div>
          ))}
          {approvalMsg && <div className="text-negRed">{approvalMsg}</div>}
        </div>
      )}

      {isAdmin && (
        <div className="ops-panel ops-matching-journal border border-border/60 rounded px-3 py-2 text-xs space-y-1">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-medium">P&amp;L reclassifications</span>
            <span className="text-muted">
              — admin journal: book a locked spread (carried parcels ⇄ real fills, equal lots) into a chosen
              month, across books, no trade moves. Reversible.
            </span>
            <span className="ops-matching-journal-controls ml-auto flex items-center gap-1">
              <input
                className="bg-transparent border border-border/60 rounded px-1.5 py-0.5 w-44"
                placeholder="product (e.g. Hungary…)"
                value={reclassProduct}
                onChange={(e) => setReclassProduct(e.target.value)}
                list="reclass-products"
              />
              <datalist id="reclass-products">
                {[...new Set(data.rows.map((r) => r.product))].map((p) => (
                  <option key={p} value={p} />
                ))}
              </datalist>
              <input
                className="bg-transparent border border-border/60 rounded px-1.5 py-0.5 w-20"
                placeholder="Q3-26"
                value={reclassPeriod}
                onChange={(e) => setReclassPeriod(e.target.value)}
              />
              <button
                className="border border-border/60 rounded px-2 py-0.5 text-muted hover:text-fg disabled:opacity-40"
                disabled={!reclassProduct.trim() || !reclassPeriod.trim()}
                onClick={() =>
                  setReclassTarget({ product: reclassProduct.trim(), period: reclassPeriod.trim() })
                }
              >
                New…
              </button>
            </span>
          </div>
          {(reclassQ.data?.entries ?? []).map((r) => (
            <div key={r.id} className="flex items-center gap-2 flex-wrap">
              <span
                className={cn(
                  "px-1.5 py-0.5 rounded text-[10px] uppercase",
                  r.status === "active" ? "bg-posGreen/15 text-posGreen" : "bg-line/60 text-muted"
                )}
              >
                {r.status}
              </span>
              <span className="font-medium">{r.name}</span>
              <span className="text-muted">
                {r.periodLabel.product} {r.periodLabel.period} · {r.lots} lots →{" "}
                {mLabel(r.targetMonth)} · {r.targetBook ?? "(no book)"} · {r.targetTrader ?? "—"}
              </span>
              <span className={cn("tabular-nums font-medium", eurClass(r.amountEur))}>{fmtEur0(r.amountEur)}</span>
              {r.stale && (
                <span
                  className="px-1 py-0.5 rounded text-[10px] bg-negRed/15 text-negRed"
                  title="an anchored trade was edited since this entry was created — review it"
                >
                  stale
                </span>
              )}
              <span className="text-muted italic" title={r.reason}>
                — {r.reason.length > 60 ? `${r.reason.slice(0, 60)}…` : r.reason}
              </span>
              <span className="text-muted">by {r.createdByName ?? "—"}</span>
              {r.status === "reversed" && r.reversalReason && (
                <span className="text-muted italic" title={r.reversalReason}>
                  reversed by {r.reversedByName ?? "—"}: {r.reversalReason.length > 40 ? `${r.reversalReason.slice(0, 40)}…` : r.reversalReason}
                </span>
              )}
              {r.status === "active" && (
                <button
                  className="text-negRed hover:underline disabled:opacity-40"
                  disabled={reverseReclass.isPending}
                  onClick={() => reverseReclass.mutate({ id: r.id })}
                >
                  reverse
                </button>
              )}
            </div>
          ))}
          {(reclassQ.data?.entries ?? []).length === 0 && (
            <div className="text-muted italic">no reclassifications</div>
          )}
          {reclassMsg && <div className="text-negRed">{reclassMsg}</div>}
        </div>
      )}

      {reclassTarget && (
        <ReclassDialog
          target={reclassTarget}
          onClose={() => setReclassTarget(null)}
          onCreated={() => {
            setReclassTarget(null);
            void qc.invalidateQueries({ queryKey: ["eex-reclass"] });
          }}
        />
      )}

      <div className="ops-toolbar ops-matching-filters flex items-center gap-2 flex-wrap text-xs">
        <button
          className={cn(
            "px-2 py-0.5 rounded border",
            monthFilter == null ? "border-accent text-fg" : "border-border/60 text-muted hover:text-fg"
          )}
          onClick={() => setMonthFilter(null)}
        >
          All months
        </button>
        {data.months.map((m) => (
          <button
            key={m.month}
            className={cn(
              "px-2 py-0.5 rounded border tabular-nums",
              monthFilter === m.month ? "border-accent text-fg" : "border-border/60 text-muted hover:text-fg"
            )}
            onClick={() => setMonthFilter(monthFilter === m.month ? null : m.month)}
            title={`unmatched default impact booking in ${mLabel(m.month)}`}
          >
            {mLabel(m.month)} <span className={eurClass(m.eur)}>{fmtCompact(m.eur)}</span>
          </button>
        ))}
        <select
          value={bookFilter}
          onChange={(e) => setBookFilter(e.target.value)}
          className="ml-auto bg-transparent border border-border/60 rounded px-1.5 py-0.5 text-xs"
        >
          <option value="">All books</option>
          {books.map((b) => (
            <option key={b} value={b}>
              {b}
            </option>
          ))}
        </select>
      </div>

      <div className="ops-table-frame overflow-auto border border-border/60 rounded-md">
        <table className="min-w-full text-xs">
          <thead className="bg-bg sticky top-0 text-muted">
            <tr>
              <Th> </Th>
              <Th>Contract</Th>
              <Th>Book</Th>
              <Th>Trader</Th>
              <Th className="text-right">Open B</Th>
              <Th className="text-right">Open S</Th>
              <Th className="text-right">Matchable</Th>
              <Th>If unmatched — books</Th>
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 && (
              <tr>
                <Td colSpan={8} className="text-muted italic py-3">
                  nothing to decide{monthFilter ? ` for ${mLabel(monthFilter)}` : ""} — the book is fully matched or
                  one-sided
                </Td>
              </tr>
            )}
            {filtered.map((r) => {
              const k = rowKey(r);
              const isOpen = open === k;
              return (
                <Fragment key={k}>
                  <tr
                    className="border-t border-border/30 hover:bg-border/10 cursor-pointer"
                    onClick={() => setOpen(isOpen ? null : k)}
                  >
                    <Td className="text-muted">{isOpen ? "▾" : "▸"}</Td>
                    <Td>
                      {r.product} <span className="font-mono">{r.period}</span>
                      {r.kind === "carried" && (
                        <span className="ml-1.5 px-1 py-0.5 rounded text-[10px] bg-sky-500/15 text-sky-600 dark:text-sky-400">
                          vs carried
                        </span>
                      )}
                      {r.kind === "real" && r.hasCarried && (
                        <span
                          className="ml-1.5 text-sky-600 dark:text-sky-400"
                          title="this contract also holds opposing carried lots"
                        >
                          ●
                        </span>
                      )}
                      {r.kind === "delivering" && (
                        <span
                          className="ml-1.5 px-1 py-0.5 rounded text-[10px] bg-line/60 text-muted"
                          title="no matching decision in this cell — its default booking is shown so nothing material hides"
                        >
                          delivering{(r.carriedLots ?? 0) > 0 ? ` · ${r.carriedLots} carried` : ""}
                        </span>
                      )}
                      {isAdmin && (
                        <button
                          className="ml-1.5 text-[10px] text-muted hover:text-fg hover:underline"
                          title="book this period's locked spread into a chosen month (admin journal)"
                          onClick={(e) => {
                            e.stopPropagation();
                            setReclassTarget({ code: r.code, expYear: r.expYear, expMonth: r.expMonth });
                          }}
                        >
                          reclassify
                        </button>
                      )}
                    </Td>
                    <Td className="text-muted">{r.book ?? "(no book)"}</Td>
                    <Td className="text-muted">{r.trader ?? "—"}</Td>
                    <Td className="text-right tabular-nums text-posGreen">
                      {r.kind === "carried" ? r.realLots || "" : r.openBuys || ""}
                    </Td>
                    <Td className="text-right tabular-nums text-negRed">
                      {r.kind === "carried" ? r.carriedLots || "" : r.openSells || ""}
                    </Td>
                    <Td className="text-right tabular-nums font-medium">
                      {r.kind === "delivering" ? "—" : r.matchable}
                    </Td>
                    <Td className="whitespace-nowrap">
                      {r.months.length === 0 ? (
                        <span className="text-muted">—</span>
                      ) : (
                        r.months.map((m, i) => (
                          <span key={m.month} className={cn(monthFilter === m.month && "font-semibold")}>
                            {i > 0 && <span className="text-muted"> · </span>}
                            <span className="text-muted">{mLabel(m.month)}</span>{" "}
                            <span className={cn("tabular-nums", eurClass(m.eur))}>{fmtEur0(m.eur)}</span>
                          </span>
                        ))
                      )}
                    </Td>
                  </tr>
                  {isOpen && (
                    <tr className="bg-bg">
                      <td colSpan={8} className="p-2">
                        <MatchingPanel
                          target={{ code: r.code, expYear: r.expYear, expMonth: r.expMonth }}
                          book={r.book}
                          trader={r.trader}
                        />
                      </td>
                    </tr>
                  )}
                </Fragment>
              );
            })}
          </tbody>
        </table>
      </div>

      <div className="text-[10px] text-muted">
        real rows: FIFO closes the matchable lots by default ·{" "}
        <span className="text-sky-600 dark:text-sky-400">vs carried</span> rows: both sides deliver by default — the
        action is a declared close · delivering rows: no decision here — shown so default bookings can&rsquo;t hide ·
        traders propose matches, admins approve; label edits run the recognition gate
      </div>
    </div>
  );
}
